import {
  ACTION,
  filterByCellValueMenuItem,
  IconName,
  useCallbackRef,
  useJsonModal,
  useMixpanel,
  type AgGridMenuItems,
  type Column,
  type ColumnDef,
  type CustomerTrade,
  type FilterableProperty,
  type UseFilterBuilderOutput,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams } from 'ag-grid-enterprise';
import { compact } from 'lodash-es';
import { useMemo, type ReactNode } from 'react';
import { useRoleAuth } from '../../../hooks/useRoleAuth';
import { CustomerTradeMenu } from './CustomerTradeMenu';
import { useCustomerTradeDrawer } from './Drawers';
import { isTradeCancelled } from './isTradeCancelled';
import { useCustomerTradeCancellation } from './useCustomerTradeCancellation';
import { colIDToFilterBuilderKey } from './useCustomerTradeFilter';

export function getCustomerTradeActionMenuItems({
  customerTrade,
  canModifyOrCancelCustomerTrade,
  onModifyTrade,
  onCancelTrade,
}: {
  customerTrade: CustomerTrade;
  canModifyOrCancelCustomerTrade: boolean;
  onModifyTrade: (customerTrade: CustomerTrade) => void;
  onCancelTrade: (customerTrade: CustomerTrade) => void;
}) {
  if (!canModifyOrCancelCustomerTrade) {
    return [];
  }

  const isCanceled = isTradeCancelled(customerTrade);
  return [
    {
      name: 'Modify Trade',
      action: () => onModifyTrade(customerTrade),
      disabled: isCanceled,
      icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
    },
    {
      name: 'Cancel Trade',
      action: () => onCancelTrade(customerTrade),
      disabled: isCanceled,
      icon: `<i class="ag-icon ${IconName.MinusCircle}"/>`,
    },
  ];
}

export function useCustomerTradeMenu({
  openClause,
  filterableProperties,
}: {
  openClause: UseFilterBuilderOutput['addAndOpenClause'];
  filterableProperties: FilterableProperty<string>[];
}): {
  columns: Column[];
  getCustomContextMenuItems: (params: GetContextMenuItemsParams) => AgGridMenuItems;
  dialogs: ReactNode;
} {
  const mixpanel = useMixpanel();
  const { isAuthorized } = useRoleAuth();
  const { openCustomerTradeCancellation } = useCustomerTradeCancellation();
  const canModifyOrCancelCustomerTrade = isAuthorized(ACTION.DEALER_TRADING);

  const { handleClickJson, jsonModal } = useJsonModal();

  const { openCustomerTradeDrawer } = useCustomerTradeDrawer();

  const dialogs = useMemo(() => <>{jsonModal}</>, [jsonModal]);

  // Modify trade
  const handleModifyTrade = useCallbackRef((customerTrade: CustomerTrade) => {
    openCustomerTradeDrawer(customerTrade);
  });

  const columns = useMemo(
    () =>
      compact([
        {
          type: 'hamburgerMenu',
          id: 'rowMenu',
          params: {
            renderItems: params => (
              <CustomerTradeMenu
                {...params}
                onModifyTrade={handleModifyTrade}
                onCancelTrade={openCustomerTradeCancellation}
                onShowJson={handleClickJson}
              />
            ),
          },
        },
      ] satisfies ColumnDef<CustomerTrade>[]),
    [openCustomerTradeCancellation, handleModifyTrade, handleClickJson]
  );

  const getCustomContextMenuItems: (params: GetContextMenuItemsParams) => AgGridMenuItems = useCallbackRef(
    (params: GetContextMenuItemsParams) => {
      const customerTrade = params?.node?.data;
      if (customerTrade == null) {
        return [];
      }

      const customerTradeActions = getCustomerTradeActionMenuItems({
        customerTrade,
        canModifyOrCancelCustomerTrade,
        onModifyTrade: handleModifyTrade,
        onCancelTrade: openCustomerTradeCancellation,
      });

      return compact([
        ...filterByCellValueMenuItem({ params, filterableProperties, openClause, colIDToFilterBuilderKey, mixpanel }),
        ...customerTradeActions,
      ]);
    }
  );

  return {
    getCustomContextMenuItems,
    columns,
    dialogs,
  };
}
